import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QrCode, Trash2 } from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardDescription, AppCardHeader, AppCardTitle,
  Callout, FileUpload, FormField, Input, Switch, Textarea,
} from "../ui";
import Shimmer from "../Common/Shimmer";
import {
  getQrSettings, removeQrImage, updateQrSettings, uploadQrImage,
} from "../../utils/qrPaymentApi";

/**
 * Settings → Payments → UPI QR.
 *
 * The institute's own QR code, for collecting without a payment gateway.
 * Students pay it from any UPI app and upload a screenshot; staff check it
 * against the bank in Payment approvals, and only then does the student get the
 * course or the receipt.
 *
 * The two switches apply at once (they are switches). The payee details are a
 * form and are saved with its button. Switching on needs something to pay to —
 * a QR image or a UPI ID — and the server refuses otherwise.
 */
export default function QrPaymentSettings({ showToast }) {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ upiId: "", payeeName: "", instructions: "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const adopt = useCallback((data) => {
    setCfg(data);
    setForm({ upiId: data.upiId || "", payeeName: data.payeeName || "", instructions: data.instructions || "" });
  }, []);

  useEffect(() => {
    getQrSettings()
      .then(({ data }) => adopt(data.data))
      .catch(() => setLoadError(true));
  }, [adopt]);

  const run = async (fn, okMessage) => {
    setBusy(true);
    try {
      const { data } = await fn();
      adopt(data.data);
      if (okMessage) showToast?.(okMessage);
      return true;
    } catch (err) {
      showToast?.(err?.response?.data?.message || "Couldn't save the UPI QR settings.", "error");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const toggle = (patch) => {
    const previous = cfg;
    setCfg((c) => ({ ...c, ...patch }));
    run(() => updateQrSettings(patch), "UPI QR settings updated.").then((ok) => { if (!ok) setCfg(previous); });
  };

  const upload = async (picked) => {
    setFiles(picked);
    if (!picked[0]) return;
    await run(() => uploadQrImage(picked[0]), "QR code uploaded.");
    setFiles([]);
  };

  const dirty = cfg && (form.upiId !== (cfg.upiId || "") || form.payeeName !== (cfg.payeeName || "") || form.instructions !== (cfg.instructions || ""));

  if (loadError) return <Callout tone="danger" className="tw-mt-4">Couldn't load the UPI QR settings.</Callout>;
  if (!cfg) return <div className="tw-mt-4"><Shimmer height={52} count={3} /></div>;

  const hasTarget = cfg.hasQrImage || !!cfg.upiId;

  return (
    <AppCard className="tw-mt-4">
      <AppCardHeader>
        <AppCardTitle>
          <span className="tw-inline-flex tw-items-center tw-gap-2">
            <QrCode size={18} aria-hidden="true" /> UPI QR payments
          </span>
        </AppCardTitle>
        <AppCardDescription>
          Collect without a payment gateway. Students pay your QR from any UPI app and upload the screenshot; you check
          it against your bank and approve it in{" "}
          <Link to="/payment-approvals" className="tw-text-primary">Payment approvals</Link>. Nothing opens for the
          student until you do.
        </AppCardDescription>
      </AppCardHeader>
      <AppCardContent className="tw-flex tw-flex-col tw-gap-5">
        <Switch
          checked={cfg.enabled}
          onChange={(enabled) => toggle({ enabled })}
          disabled={busy || (!cfg.enabled && !hasTarget)}
          label="Take payments by UPI QR"
          description={hasTarget
            ? "Offered on fees and on every paid course students can enrol in themselves — untick “Accept UPI QR payments” in a course's settings to leave that course out."
            : "Upload your QR code or add a UPI ID below first."}
        />

        <div className="tw-grid tw-gap-5 md:tw-grid-cols-[minmax(0,14rem)_minmax(0,1fr)]">
          <section aria-label="QR code" className="tw-flex tw-flex-col tw-gap-2">
            <span className="tw-text-sm tw-font-semibold tw-text-content">QR code</span>
            {cfg.qrImageUrl ? (
              <>
                <img
                  src={cfg.qrImageUrl}
                  alt="Your UPI QR code"
                  className="tw-w-full tw-max-w-[14rem] tw-rounded-md tw-border tw-border-solid tw-border-line"
                />
                <div className="tw-flex tw-gap-2">
                  <AppButton variant="ghost" size="sm" onClick={() => run(removeQrImage, "QR code removed.")} disabled={busy}>
                    <Trash2 size={14} /> Remove
                  </AppButton>
                </div>
              </>
            ) : null}
            <FileUpload
              files={files}
              onChange={upload}
              accept=".png,.jpg,.jpeg,.webp"
              multiple={false}
              maxFiles={1}
              maxSizeMB={5}
              disabled={busy}
              hint={cfg.qrImageUrl ? "Replace with a new QR image" : "Drop your QR code image here (PNG or JPG)"}
            />
            <span className="tw-text-xs tw-text-content-muted">
              Download it from your bank or UPI business app (PhonePe Business, Google Pay for Business, Paytm for Business…).
            </span>
          </section>

          <form
            className="tw-flex tw-min-w-0 tw-flex-col tw-gap-3"
            onSubmit={(e) => { e.preventDefault(); run(() => updateQrSettings(form), "Payee details saved."); }}
          >
            <FormField label="UPI ID" hint="Shown under the QR, and opens the student's UPI app on a phone with the amount filled in.">
              <Input value={form.upiId} onChange={(e) => setForm((f) => ({ ...f, upiId: e.target.value }))} placeholder="yourinstitute@okaxis" autoComplete="off" />
            </FormField>
            <FormField label="Payee name" hint="The name students should see in their UPI app before they pay.">
              <Input value={form.payeeName} maxLength={100} onChange={(e) => setForm((f) => ({ ...f, payeeName: e.target.value }))} placeholder="Sunrise Academy" />
            </FormField>
            <FormField label="Instructions for students" hint="Optional — e.g. “Write your name and course in the payment note.”">
              <Textarea rows={3} maxLength={1000} value={form.instructions} onChange={(e) => setForm((f) => ({ ...f, instructions: e.target.value }))} />
            </FormField>
            <div>
              <AppButton type="submit" disabled={busy || !dirty}>Save details</AppButton>
            </div>
          </form>
        </div>

        <Switch
          checked={cfg.feeDues}
          onChange={(feeDues) => toggle({ feeDues })}
          disabled={busy}
          label="Also for fees students already owe"
          description="Shows “Pay by UPI QR” on My Fees for enrolled students' balances and EMIs."
        />

        {cfg.razorpayEnabled && (
          <Callout tone="info">
            Razorpay is also on. Students see both: online payment first, and UPI QR as the alternative.
          </Callout>
        )}
      </AppCardContent>
    </AppCard>
  );
}
